/**
 * The GAM orders the monthly benchmark refresh leaves out — test and house
 * orders that would otherwise count as campaigns (Oct 2026: a 1M-impression
 * "Generic Order [PLEASE IGNORE]" skewed the skippable benchmark). Shared by
 * both benchmark tabs; a change applies from the next refresh.
 */

import React, { useState } from 'react';
import { Button } from '../../ui';
import { ExcludedBenchmarkOrder, saveExcludedBenchmarkOrders } from '../../../services/adBenchmarksApi';
import { formatInstant } from './benchmarkRefresh';

interface ExcludedOrdersPanelProps {
  orders: ExcludedBenchmarkOrder[];
  onSaved: (orders: ExcludedBenchmarkOrder[]) => void;
  notify: (kind: 'success' | 'error', title: string, message?: string) => void;
}

const panelStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  padding: '10px 14px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13,
  color: 'var(--fg-1)',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 9px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-1)',
  fontSize: 13,
  fontFamily: 'var(--font-sans)',
  background: 'var(--bg-surface-1)',
  color: 'var(--fg-1)',
};

const orderName = (order: ExcludedBenchmarkOrder): string => order.label || `Order ${order.orderId}`;

const ExcludedOrdersPanel: React.FC<ExcludedOrdersPanelProps> = ({ orders, onSaved, notify }) => {
  const [open, setOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async (next: { orderId: string; label?: string }[], done: { title: string; message: string }) => {
    setSaving(true);
    try {
      const { excludedOrders } = await saveExcludedBenchmarkOrders(next);
      onSaved(excludedOrders);
      notify('success', done.title, done.message);
      return true;
    } catch (err: any) {
      notify('error', 'Couldn’t save the excluded orders', err?.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const kept = orders.map((order) => ({ orderId: order.orderId, label: order.label }));
  const trimmedId = orderId.trim();

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!/^\d+$/.test(trimmedId)) {
      notify('error', 'Enter the GAM order ID', 'It’s all digits — the number in the order’s GAM URL.');
      return;
    }
    if (orders.some((order) => order.orderId === trimmedId)) {
      notify('error', 'Already left out', `Order ${trimmedId} is already on the list.`);
      return;
    }
    const added = await save([...kept, { orderId: trimmedId, ...(label.trim() ? { label: label.trim() } : {}) }], {
      title: 'Order left out of the benchmarks',
      message: 'It applies from the next GAM refresh.',
    });
    if (added) {
      setOrderId('');
      setLabel('');
    }
  };

  const remove = (id: string) =>
    save(
      kept.filter((order) => order.orderId !== id),
      { title: 'Order counted again', message: 'It counts from the next GAM refresh.' },
    );

  const summary =
    orders.length === 0
      ? 'No orders are left out of the GAM refresh.'
      : `Left out of the GAM refresh: ${orders.map(orderName).join(', ')}`;

  return (
    <div style={panelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ color: 'var(--fg-3)' }}>{summary}</span>
        <Button variant="ghost" size="sm" onClick={() => setOpen((prev) => !prev)} aria-expanded={open}>
          {open ? 'Done' : 'Manage excluded orders'}
        </Button>
      </div>
      {open && (
        <>
          <p style={{ margin: 0, fontSize: 12.5, color: 'var(--fg-3)' }}>
            Test and house orders that shouldn’t count as campaigns. Changes apply from the next GAM refresh, on
            both tabs.
          </p>
          {orders.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {orders.map((order) => (
                <li key={order.orderId} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                  <span>
                    <strong>{orderName(order)}</strong>
                    <span style={{ color: 'var(--fg-4)', fontSize: 12 }}>
                      {' '}
                      · {order.orderId} · added by {order.addedBy}
                      {formatInstant(order.addedAt) ? ` on ${formatInstant(order.addedAt)}` : ''}
                    </span>
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => remove(order.orderId)}
                    disabled={saving}
                    aria-label={`Count order ${order.orderId} again`}
                  >
                    Remove
                  </Button>
                </li>
              ))}
            </ul>
          )}
          <form onSubmit={add} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            <input
              style={{ ...inputStyle, width: 150 }}
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="GAM order ID"
              aria-label="GAM order ID"
              inputMode="numeric"
            />
            <input
              style={{ ...inputStyle, flex: '1 1 220px' }}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Why it’s left out (optional — defaults to the GAM order name)"
              aria-label="Why it’s left out"
              maxLength={160}
            />
            <Button type="submit" size="sm" disabled={saving || trimmedId === ''}>
              {saving ? 'Saving…' : 'Leave out'}
            </Button>
          </form>
        </>
      )}
    </div>
  );
};

export default ExcludedOrdersPanel;
